// /admin/v2/videos/approve — one card per imported video (BRIEFS C11.9).
//
// Each card shows the video part, its topics and description, and the places in the notes
// the AI proposed (tick / untick; add another unit with the unit picker). Approve = the
// video is placed there and Harsh sees it (a "Concept video: …" / "Question video: …" line
// in those units, and in Search). The card shows the kind (C12.1) and the owner can change
// it before approving. Reject = it stays hidden (kept, can be approved later).
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { v2VideoApprove, v2VideoReject, v2VideoReview, errText } from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';
import { KindChoice, KIND_TEXT, UnitPicker } from './AdminV2Clips';

const muted = { fontSize: 12, color: 'var(--text-muted)' };
const FILTERS = [
  { key: 'proposed', label: 'Waiting' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];
const unitText = (u) => (u.number ? `${u.number} ${u.title}` : u.title || u.code);

function VideoCard({ clip, onDone }) {
  const proposed = clip.proposals.filter((p) => !p.is_removed);
  const current = clip.units.filter((u) => !u.is_removed);
  const start = clip.status === 'approved' && current.length
    ? current.map((u) => u.code)
    : proposed.filter((p) => p.confidence == null || p.confidence >= 0.5).map((p) => p.code);
  const [ticked, setTicked] = useState(new Set(start));
  const [extra, setExtra] = useState(
    clip.status === 'approved' ? current.filter((u) => !proposed.some((p) => p.code === u.code)) : []);
  const [kind, setKind] = useState(clip.kind === 'question' ? 'question' : 'concept');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (code) => {
    const t = new Set(ticked);
    if (t.has(code)) t.delete(code); else t.add(code);
    setTicked(t);
  };
  const chosen = [...proposed.filter((p) => ticked.has(p.code)).map((p) => p.code), ...extra.map((u) => u.code)];
  const act = async (fn, what) => {
    setBusy(true); setErr('');
    try { const out = await fn(); onDone(out, what); } catch (x) { setErr(errText(x)); } finally { setBusy(false); }
  };

  return (
    <div className="card" data-testid={`v2-video-card-${clip.id}`} style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 380px' }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>{clip.title}</div>
          <div data-testid={`v2-video-kind-${clip.id}`} style={{ fontSize: 13, marginTop: 2 }}>
            <span className={`badge ${kind === 'question' ? 'badge-yellow' : 'badge-blue'}`}>{KIND_TEXT[kind]}</span>
          </div>
          {clip.description && <div style={{ fontSize: 14, marginTop: 4 }}>{clip.description}</div>}
          <div style={{ ...muted, marginTop: 6 }}>
            Part {clip.start_text}–{clip.end_text} · {clip.minutes} min ·{' '}
            <a href={clip.url} target="_blank" rel="noreferrer">Open on YouTube at {clip.start_text}</a>
            {clip.chapter ? ` · ${clip.chapter}` : ' · no chapter given'}
            {clip.row_no ? ` · row ${clip.row_no}` : ''}
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: clip.status === 'approved' ? '#047857' : clip.status === 'rejected' ? '#B91C1C' : '#B45309' }}>
          {clip.status === 'proposed' ? 'Waiting for approval' : clip.status === 'approved' ? 'Approved — Harsh sees it' : 'Rejected — hidden'}
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={muted}>Places in the notes{proposed.length ? ' proposed by the AI (tick the ones to use)' : ''}</div>
        {proposed.length === 0 && <div style={{ fontSize: 14, marginTop: 4 }}>No place proposed yet. Add one below.</div>}
        {proposed.map((p) => (
          <label key={p.code} data-testid={`v2-video-place-${clip.id}-${p.code}`} style={{
            display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer',
          }}>
            <input type="checkbox" checked={ticked.has(p.code)} onChange={() => toggle(p.code)} style={{ marginTop: 4 }} />
            <span style={{ fontSize: 14 }}>
              <strong>{p.code}</strong> · {unitText(p)}
              {p.confidence != null && <span style={{ ...muted, marginLeft: 6 }}>{Math.round(p.confidence * 100)}% sure</span>}
              {p.reason && <div style={muted}>{p.reason}</div>}
            </span>
          </label>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={muted}>Kind — change it here before you approve, if it is wrong</div>
        <KindChoice value={kind} onChange={setKind} testid={`v2-video-kindchoice-${clip.id}`} />
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={muted}>Add another unit</div>
        <UnitPicker value={extra} onChange={setExtra} />
      </div>
      {err && <div role="alert" style={{ color: '#B91C1C', marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" disabled={busy || chosen.length === 0}
          onClick={() => act(() => v2VideoApprove(clip.id, chosen, kind), 'approved')} data-testid={`v2-video-approve-${clip.id}`}>
          {clip.status === 'approved' ? 'Save places' : `Approve${chosen.length ? ` (${chosen.length} ${chosen.length === 1 ? 'place' : 'places'})` : ''}`}
        </button>
        {clip.status !== 'rejected' && (
          <button type="button" className="btn btn-ghost" disabled={busy}
            onClick={() => act(() => v2VideoReject(clip.id), 'rejected')} data-testid={`v2-video-reject-${clip.id}`}>
            {clip.status === 'approved' ? 'Hide (reject)' : 'Reject'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminV2VideoApprove() {
  const [status, setStatus] = useState('proposed');
  const [msg, setMsg] = useState(null);
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['v2-video-review', status], queryFn: () => v2VideoReview(status),
  });
  const done = (out, what) => {
    setMsg({ kind: 'ok', text: what === 'approved'
      ? `"${out.title}" is approved: it shows in ${out.units.map((u) => u.code).join(', ')} and in Search.`
      : `"${out.title}" is rejected: Harsh does not see it.` });
    qc.invalidateQueries({ queryKey: ['v2-video-review'] });
  };
  const counts = data ? data.counts : {};

  return (
    <div>
      <V2Tabs title="Videos: approve"
        intro="Each imported video shows the places the AI proposed. Tick the right places (or add a unit), then press Approve. Only approved videos show to Harsh: in the notes as “Watch video · N min”, and in Search." />
      <Msg msg={msg} />
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f.key} type="button" className={`btn btn-sm ${status === f.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setStatus(f.key); setMsg(null); }} data-testid={`v2-video-filter-${f.key}`}>
            {f.label}{counts[f.key] != null ? ` (${counts[f.key]})` : ''}
          </button>
        ))}
      </div>
      {isLoading && <div className="card">Loading…</div>}
      {isError && <div className="card" style={{ color: '#B91C1C' }}>{errText(error)}</div>}
      {data && data.clips.length === 0 && (
        <div className="card" style={{ color: 'var(--text-muted)' }}>
          {status === 'proposed' ? 'No video is waiting. Import your table on “Videos: import”.' : 'None.'}
        </div>
      )}
      {data && data.clips.map((c) => <VideoCard key={`${c.id}-${c.status}`} clip={c} onDone={done} />)}
    </div>
  );
}
